/**
 * Centralized App Store / Play Store link behavior (spec item 1).
 *
 * Every "Get the App" surface must go through here so referral handling
 * can't be forgotten when a new button is added: when a referral is active,
 * an App Store click first drops `TCMCode=ABC123` into the clipboard (the
 * iOS handoff — iOS has no install-referrer API), then navigation proceeds
 * normally. Nothing here ever blocks or delays navigation.
 */
import analytics from './analytics';
import {
  extractTcmCode,
  getPendingReferralCode,
  normalizeReferralCode,
} from './referralState';

export const APP_STORE_URL =
  'https://apps.apple.com/us/app/the-crapper-mapper/id6756213834';
export const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.thecrappermapper.crappermapper';

/** Play URL with the invite attached for Install Referrer attribution. */
export const getPlayStoreUrl = (referralCode) => {
  const normalized = normalizeReferralCode(referralCode);
  if (!normalized) return PLAY_STORE_URL;
  return `${PLAY_STORE_URL}&referrer=${encodeURIComponent(`ref=${normalized}`)}`;
};

const writeClipboard = async (text) => {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (error) {
    console.warn('Clipboard copy failed:', error);
  }
  // Legacy fallback (older browsers / non-secure contexts).
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch (error) {
    console.warn('Clipboard fallback failed:', error);
    return false;
  }
};

/**
 * Active referral code if the user arrived through a referral link.
 * Exported for screens that display referral state (landing pages).
 */
export const getActiveReferralCode = () => getPendingReferralCode();

/**
 * onClick handler for App Store links/ buttons. Copies `TCMCode=<CODE>`
 * when a referral is active, tracks the click, and always lets the
 * navigation proceed.
 */
export const handleAppStoreClick = async () => {
  const code = getPendingReferralCode();
  if (code) {
    await writeClipboard(`TCMCode=${code}`);
  }
  analytics.track('referral_store_clicked', {
    store: 'app_store',
    referral_code: code,
  });
};

/** onClick handler for Play Store links (attribution rides the referrer). */
export const handlePlayStoreClick = () => {
  analytics.track('referral_store_clicked', {
    store: 'play_store',
    referral_code: getPendingReferralCode(),
  });
};

// Re-exported for the accept flow and tests.
export { extractTcmCode, normalizeReferralCode };
